import { forwardRef, useState, useEffect, useMemo } from 'react'
import { NavLink } from 'react-router-dom'
import { useRecoilState, useRecoilValue } from 'recoil'
import clsx from 'clsx'
import PositionInfo from '@/components/trade/PositionInfo'
import OrderFormCheckbox from '@/components/trade/OrderFormCheckbox'
import OrderList from '@/components/trade/OrderList'
import IconHistory from '@/components/icons/IconHistory'
import {
  marketSymbolState,
  marketIsReadyValue,
  marketInfoValue
} from '@/recoil/Market'
import { latestNotificationValue } from '@/recoil/Notification'
import { allOpenOrderListState, openOrderListValue } from '@/recoil/OrderList'
import { useWalletSelector } from '@/contexts/WalletSelectorContext'
import fetcher from '@/api/fetcher'
import debounce from '@/utils/debounce'

import './Orders.css'

const ORDER_TAB_LIST = ['ALL_OPEN_ORDERS', 'FILL_ORDERS']
const FUTURES_ORDER_TAB_LIST = ['POSITIONS'].concat(ORDER_TAB_LIST)
const ORDER_TAB_LABEL_MAP = {
  POSITIONS: 'Positions / P&L',
  ALL_OPEN_ORDERS: 'Orders',
  FILL_ORDERS: 'Trade History'
}

export default forwardRef(function Orders({ children, ...props }, ref) {
  props = {
    ref,
    ...props,
    className: `${props.className} card orders-list`
  }

  const { walletReady, modal } = useWalletSelector()
  const [isLoading, setIsLoading] = useState(false)
  const [marketSymbol] = useRecoilState(marketSymbolState)
  const [allOpenOrderList] = useRecoilState(allOpenOrderListState)
  const openOrderList = useRecoilValue(openOrderListValue)
  const marketIsReady = useRecoilValue(marketIsReadyValue)
  const marketInfo = useRecoilValue(marketInfoValue)
  const latestNotification = useRecoilValue(latestNotificationValue)
  const [orderTypeTab, setOrderTypeTab] = useState(
    marketInfo.isFutures ? 'POSITIONS' : 'ALL_OPEN_ORDERS'
  )
  const [orderListMap, setOrderListMap] = useState({})
  const [showAllMarketData, setShowAllMarketData] = useState(true)
  const appIsReady = marketIsReady && walletReady
  const tabList = marketInfo.isFutures ? FUTURES_ORDER_TAB_LIST : ORDER_TAB_LIST

  const connectWallet = () => modal.show()

  const debounceUpdateOrderList = useMemo(
    () =>
      debounce(marketSymbol => {
        setIsLoading(true)
        const params = {
          status: 'FILLED',
          size: 100
        }
        if (!showAllMarketData) {
          params.symbol = marketSymbol
        }
        fetcher.getOrders(params).then(({ meta, rows }) => {
          setOrderListMap(v => {
            v = JSON.parse(JSON.stringify(v))
            v.FILL_ORDERS = rows
            return v
          })
          setIsLoading(false)
        })
      }, 100),
    [showAllMarketData]
  )

  useEffect(() => {
    if (!appIsReady) {
      return
    }
    setOrderTypeTab(tabList[0])
  }, [appIsReady, tabList])

  useEffect(() => {
    appIsReady &&
      orderTypeTab === 'FILL_ORDERS' &&
      debounceUpdateOrderList(marketSymbol)
  }, [appIsReady, orderTypeTab, latestNotification, marketSymbol, debounceUpdateOrderList])

  useEffect(() => {
    appIsReady &&
      setOrderListMap(v => {
        v = JSON.parse(JSON.stringify(v))
        v.OPEN_ORDERS = openOrderList
        return v
      })
    setIsLoading(false)
  }, [appIsReady, orderTypeTab, openOrderList])

  useEffect(() => {
    appIsReady &&
      setOrderListMap(v => {
        v = JSON.parse(JSON.stringify(v))
        v.ALL_OPEN_ORDERS = allOpenOrderList
        return v
      })
    setIsLoading(false)
  }, [appIsReady, orderTypeTab, allOpenOrderList])

  const isShowPositionInfo = orderTypeTab === 'POSITIONS'
  const OrderTabList = tabList.map(key => (
    <div
      key={key}
      className={clsx('orders-type-tab', {
        active: key === orderTypeTab
      })}
      onClick={() => orderTypeTab !== key && setOrderTypeTab(key)}
    >
      {ORDER_TAB_LABEL_MAP[key]}
    </div>
  ))

  const orderType =
    !isShowPositionInfo && orderTypeTab === 'ALL_OPEN_ORDERS'
      ? showAllMarketData
        ? 'ALL_OPEN_ORDERS'
        : 'OPEN_ORDERS'
      : orderTypeTab

  const filterLabelText = isShowPositionInfo ? 'All Positions' : 'All Markets'

  return (
    <div {...props}>
      <div className="card-draggable-area card-title orders-header">
        <div className="orders-header-container">
          <div
            className="orders-tab-container noselect"
            onMouseDown={e => e.stopPropagation()}
          >
            {OrderTabList}
          </div>
          <div className="empty-fill-box" />
          <OrderFormCheckbox
            label={filterLabelText}
            value={showAllMarketData}
            onUpdate={() => setShowAllMarketData(v => !v)}
          />
          <NavLink
            className="orders-icon-button-link"
            to="/orders"
            onMouseDown={e => e.stopPropagation()}
          >
            <IconHistory />
          </NavLink>
        </div>
      </div>
      {walletReady ? (
        isShowPositionInfo ? (
          <PositionInfo showAllMarketData={showAllMarketData} />
        ) : (
          <OrderList
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            orderType={orderType}
            orderList={orderListMap[orderType] || []}
          />
        )
      ) : (
        <div className="wallet-connect-view">
          <div className="wallet-connect-view-discription">
            <span
              className="connect-wallet-text-button"
              onClick={connectWallet}
            >
              Connect Wallet
            </span>{' '}
            to view this content
          </div>
        </div>
      )}
      {children}
    </div>
  )
})
